(() => {
  const initialized = new WeakSet();

  const initProductGallery = (root = document) => {
    root.querySelectorAll('[data-vq-product-gallery]').forEach((gallery) => {
      if (initialized.has(gallery)) return;
      initialized.add(gallery);

      const track = gallery.querySelector('[data-vq-gallery-track]');
      const slides = Array.from(gallery.querySelectorAll('[data-vq-gallery-slide]'));
      const thumbs = Array.from(gallery.querySelectorAll('[data-vq-gallery-thumb]'));
      const prev = gallery.querySelector('[data-vq-gallery-prev]');
      const next = gallery.querySelector('[data-vq-gallery-next]');
      const galleryMain = gallery.querySelector('.vq-product-information__gallery-main');

      if (!track || !slides.length) return;

      let index = Math.max(
        0,
        slides.findIndex((slide) => slide.getAttribute('data-active') === 'true')
      );

      const render = () => {
        track.style.transform = `translateX(-${index * 100}%)`;

        slides.forEach((slide, slideIndex) => {
          const isActive = slideIndex === index;
          slide.toggleAttribute('data-active', isActive);
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });

        thumbs.forEach((thumb, thumbIndex) => {
          const isActive = thumbIndex === index;
          thumb.toggleAttribute('data-active', isActive);
          thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
        });

        if (prev) prev.disabled = index === 0;
        if (next) next.disabled = index === slides.length - 1;
      };

      prev?.addEventListener('click', () => {
        if (index === 0) return;
        index -= 1;
        render();
      });

      next?.addEventListener('click', () => {
        if (index >= slides.length - 1) return;
        index += 1;
        render();
      });

      thumbs.forEach((thumb, thumbIndex) => {
        thumb.addEventListener('click', () => {
          index = thumbIndex;
          render();
        });
      });

      let touchStartX = null;
      let touchStartY = null;
      const swipeThreshold = 40;

      galleryMain?.addEventListener(
        'touchstart',
        (event) => {
          const touch = event.touches?.[0];
          if (!touch) return;
          touchStartX = touch.clientX;
          touchStartY = touch.clientY;
        },
        { passive: true }
      );

      galleryMain?.addEventListener(
        'touchend',
        (event) => {
          const touch = event.changedTouches?.[0];
          if (!touch || touchStartX === null || touchStartY === null) return;

          const deltaX = touch.clientX - touchStartX;
          const deltaY = touch.clientY - touchStartY;
          touchStartX = null;
          touchStartY = null;

          if (Math.abs(deltaX) < swipeThreshold || Math.abs(deltaX) <= Math.abs(deltaY)) return;

          if (deltaX > 0 && index > 0) {
            index -= 1;
            render();
          } else if (deltaX < 0 && index < slides.length - 1) {
            index += 1;
            render();
          }
        },
        { passive: true }
      );

      const section = gallery.closest('.shopify-section');
      section?.addEventListener('variant:update', (event) => {
        const updatedGallery = event.detail?.data?.html?.querySelector?.(`#${gallery.id}`);
        if (!(updatedGallery instanceof HTMLElement)) return;

        gallery.replaceWith(updatedGallery);
        initProductGallery(section);
      });

      render();
    });
  };

  const initDescriptionTabs = (root = document) => {
    root.querySelectorAll('[data-vq-product-description]').forEach((section) => {
      if (initialized.has(section)) return;
      initialized.add(section);

      const desktopTabs = Array.from(section.querySelectorAll('[data-vq-description-desktop-tab]'));
      const desktopPanels = Array.from(section.querySelectorAll('[data-vq-description-desktop-panel]'));
      const accordionTriggers = Array.from(section.querySelectorAll('[data-vq-description-accordion-trigger]'));
      const accordionPanels = Array.from(section.querySelectorAll('[data-vq-description-accordion-panel]'));

      if (!desktopTabs.length && !accordionTriggers.length) return;

      const select = (index) => {
        desktopTabs.forEach((tab, tabIndex) => {
          const isActive = tabIndex === index;
          tab.toggleAttribute('data-active', isActive);
          tab.setAttribute('aria-expanded', isActive ? 'true' : 'false');
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        desktopPanels.forEach((panel, panelIndex) => {
          const isActive = panelIndex === index;
          panel.toggleAttribute('data-active', isActive);
          panel.hidden = !isActive;
        });

        accordionTriggers.forEach((trigger, triggerIndex) => {
          const isActive = triggerIndex === index;
          trigger.toggleAttribute('data-active', isActive);
          trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
        });

        accordionPanels.forEach((panel, panelIndex) => {
          const isActive = panelIndex === index;
          panel.toggleAttribute('data-active', isActive);
          panel.hidden = !isActive;
        });
      };

      const collapseAccordion = () => {
        accordionTriggers.forEach((trigger) => {
          trigger.removeAttribute('data-active');
          trigger.setAttribute('aria-expanded', 'false');
        });

        accordionPanels.forEach((panel) => {
          panel.removeAttribute('data-active');
          panel.hidden = true;
        });
      };

      desktopTabs.forEach((tab, index) => {
        tab.addEventListener('click', () => select(index));
      });

      accordionTriggers.forEach((trigger, index) => {
        trigger.addEventListener('click', () => {
          if (trigger.hasAttribute('data-active')) {
            collapseAccordion();
            return;
          }

          select(index);
        });
      });

      const defaultDesktopIndex = desktopTabs.findIndex((tab) => tab.hasAttribute('data-active'));
      const defaultAccordionIndex = accordionTriggers.findIndex((trigger) => trigger.hasAttribute('data-active'));
      const defaultIndex = Math.max(defaultDesktopIndex, defaultAccordionIndex, 0);

      select(defaultIndex);
    });
  };

  const initRecommendedProducts = (root = document) => {
    root.querySelectorAll('[data-vq-recommended-products]').forEach((section) => {
      if (initialized.has(section)) return;
      initialized.add(section);

      const track = section.querySelector('[data-vq-recommended-track]');
      const items = Array.from(section.querySelectorAll('[data-vq-recommended-item]'));
      const prev = section.querySelector('[data-vq-recommended-prev]');
      const next = section.querySelector('[data-vq-recommended-next]');

      if (!track || items.length < 2) return;

      const getStep = () => {
        const first = items[0];
        if (!(first instanceof HTMLElement)) return 320;
        const styles = window.getComputedStyle(track);
        const gap = parseFloat(styles.columnGap || styles.gap || '0');
        return first.getBoundingClientRect().width + gap;
      };

      const update = () => {
        const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
        if (prev) prev.disabled = track.scrollLeft <= 1;
        if (next) next.disabled = track.scrollLeft >= maxScroll - 1;
      };

      prev?.addEventListener('click', () => {
        track.scrollBy({ left: -getStep(), behavior: 'smooth' });
      });

      next?.addEventListener('click', () => {
        track.scrollBy({ left: getStep(), behavior: 'smooth' });
      });

      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  };

  const initStickyAddToCartViewportOffset = (root = document) => {
    root.querySelectorAll('.vq-sticky-add-to-cart__bar').forEach((bar) => {
      if (initialized.has(bar)) return;
      initialized.add(bar);

      const updateOffset = () => {
        const viewport = window.visualViewport;

        if (!viewport || window.innerWidth >= 750) {
          bar.style.setProperty('--vq-sticky-bottom-offset', '0px');
          return;
        }

        const offset = Math.max(window.innerHeight - viewport.height - viewport.offsetTop, 0);
        bar.style.setProperty('--vq-sticky-bottom-offset', `${offset}px`);
      };

      window.visualViewport?.addEventListener('resize', updateOffset);
      window.visualViewport?.addEventListener('scroll', updateOffset);
      window.addEventListener('resize', updateOffset);
      updateOffset();
    });
  };

  const initSetConfigurators = (root = document) => {
    root.querySelectorAll('[data-vq-set-config-section]').forEach((section) => {
      if (initialized.has(section)) return;
      initialized.add(section);

      const config = section.querySelector('[data-vq-set-config]');
      if (!(config instanceof HTMLElement)) return;

      const productForm = section.querySelector('product-form-component');
      const fields = Array.from(config.querySelectorAll('[data-vq-set-field]'));
      const variantRules = Array.from(config.querySelectorAll('[data-vq-set-variant-rule]'));
      const variantPicker = section.querySelector('variant-picker');

      const getVariantInput = () => productForm?.querySelector('input[name="id"]');
      const getMainAddToCartButton = () => productForm?.querySelector('[ref="addToCartButton"]');
      const getStickyAddToCartButton = () => section.querySelector('sticky-add-to-cart [ref="addToCartButton"]');
      const getCurrentVariantTitle = () => {
        const checkedRadio = variantPicker?.querySelector('input[type="radio"]:checked');
        if (checkedRadio instanceof HTMLInputElement && checkedRadio.value.trim() !== '') {
          return checkedRadio.value.trim();
        }

        const selectedOption = variantPicker?.querySelector('select[name^="options["]');
        if (selectedOption instanceof HTMLSelectElement && selectedOption.value.trim() !== '') {
          return selectedOption.value.trim();
        }

        return section.dataset.currentVariantTitle || '';
      };
      const getActiveFields = () =>
        fields.filter((field) => {
          const input = field.querySelector('[data-vq-set-value]');
          return !field.hidden && input instanceof HTMLInputElement && input.disabled === false;
        });
      const getSelectionCountForVariantTitle = (variantTitle = '') => {
        const normalizedVariantTitle = variantTitle.trim().toLowerCase();
        const matchingRule = variantRules.find(
          (rule) => (rule.dataset.variantTitle || '').trim().toLowerCase() === normalizedVariantTitle
        );

        return Number.parseInt(matchingRule?.dataset.selectionCount || '0', 10) || 0;
      };

      const closeOtherSelects = (currentSelect = null) => {
        section.querySelectorAll('[data-vq-set-select][open]').forEach((select) => {
          if (select !== currentSelect) {
            select.removeAttribute('open');
          }
        });
      };

      const syncFieldState = (field) => {
        if (!(field instanceof HTMLElement)) return;

        const hiddenInput = field.querySelector('[data-vq-set-value]');
        const toggleText = field.querySelector('[data-vq-set-toggle-text]');
        const toggleImageWrapper = field.querySelector('[data-vq-set-toggle-image-wrapper]');
        const toggleImage = field.querySelector('[data-vq-set-toggle-image]');
        const options = Array.from(field.querySelectorAll('[data-vq-set-option]'));
        const placeholder = field.dataset.placeholder || 'Bitte wählen';

        if (!(hiddenInput instanceof HTMLInputElement)) return;

        const selectedValue = hiddenInput.value.trim();
        const selectedOption = options.find((option) => option.dataset.value === selectedValue) || null;

        options.forEach((option) => {
          const isSelected = option === selectedOption;
          option.toggleAttribute('data-selected', isSelected);
          option.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });

        if (toggleText) {
          toggleText.textContent = selectedOption?.dataset.value || placeholder;
        }

        if (toggleImageWrapper instanceof HTMLElement && toggleImage instanceof HTMLImageElement) {
          const imageUrl = selectedOption?.dataset.imageUrl || '';

          if (imageUrl) {
            toggleImage.src = imageUrl;
            toggleImage.alt = selectedOption?.dataset.value || '';
            toggleImageWrapper.hidden = false;
          } else {
            toggleImage.src = '';
            toggleImage.alt = '';
            toggleImageWrapper.hidden = true;
          }
        }
      };

      const validate = () => {
        const variantInput = getVariantInput();
        const variantAvailable = Boolean(variantInput && !variantInput.disabled && variantInput.value);
        const activeFields = getActiveFields();
        const requiresConfiguration = activeFields.length > 0;
        const isComplete = requiresConfiguration
          ? activeFields.every((field) => {
              const input = field.querySelector('[data-vq-set-value]');
              return input instanceof HTMLInputElement && input.value.trim() !== '';
            })
          : true;
        const shouldDisable = !variantAvailable || !isComplete;

        config.dataset.complete = isComplete ? 'true' : 'false';

        [getMainAddToCartButton(), getStickyAddToCartButton()].forEach((button) => {
          if (button instanceof HTMLButtonElement) {
            button.disabled = shouldDisable;
          }
        });
      };

      const setFieldActiveState = (field, isActive) => {
        const input = field.querySelector('[data-vq-set-value]');
        const select = field.querySelector('[data-vq-set-select]');

        field.hidden = !isActive;
        field.style.display = isActive ? '' : 'none';

        if (input instanceof HTMLInputElement) {
          if (!isActive) {
            input.value = '';
          }
          input.disabled = !isActive;
        }

        if (!isActive && select instanceof HTMLElement) {
          select.removeAttribute('open');
        }

        syncFieldState(field);
      };

      const updateActiveFields = (variantTitle = '') => {
        const activeCount = getSelectionCountForVariantTitle(variantTitle);
        const hasActiveConfiguration = activeCount > 0;

        config.hidden = !hasActiveConfiguration;
        config.style.display = hasActiveConfiguration ? '' : 'none';

        fields.forEach((field, index) => {
          const isActive = hasActiveConfiguration && index < activeCount;
          setFieldActiveState(field, isActive);
        });

        section.classList.toggle('vq-product-information--has-active-set-config', hasActiveConfiguration);
        validate();
      };

      fields.forEach((field) => {
        syncFieldState(field);

        field.querySelectorAll('[data-vq-set-option]').forEach((option) => {
          option.addEventListener('click', () => {
            const hiddenInput = field.querySelector('[data-vq-set-value]');
            if (!(hiddenInput instanceof HTMLInputElement)) return;

            hiddenInput.value = option.dataset.value || '';
            syncFieldState(field);

            const select = field.querySelector('[data-vq-set-select]');
            if (select instanceof HTMLElement) {
              select.removeAttribute('open');
            }

            validate();
          });
        });
      });

      section.querySelectorAll('[data-vq-set-select]').forEach((select) => {
        select.addEventListener('toggle', () => {
          if (select.hasAttribute('open')) {
            closeOtherSelects(select);
          }
        });
      });

      document.addEventListener('click', (event) => {
        if (section.contains(event.target)) return;
        closeOtherSelects();
      });

      section.addEventListener('variant:update', (event) => {
        const variantTitle = event.detail?.resource?.title || getCurrentVariantTitle();
        section.dataset.currentVariantTitle = variantTitle;
        updateActiveFields(variantTitle);
        window.requestAnimationFrame(validate);
      });

      section.addEventListener('variant:selected', (event) => {
        const variantTitle = event.detail?.resource?.title || getCurrentVariantTitle();
        section.dataset.currentVariantTitle = variantTitle;
        updateActiveFields(variantTitle);
      });

      variantPicker?.addEventListener('change', () => {
        const variantTitle = getCurrentVariantTitle();
        section.dataset.currentVariantTitle = variantTitle;
        updateActiveFields(variantTitle);
      });

      updateActiveFields(getCurrentVariantTitle());
      validate();
    });
  };

  const initAll = (root = document) => {
    initProductGallery(root);
    initDescriptionTabs(root);
    initRecommendedProducts(root);
    initStickyAddToCartViewportOffset(root);
    initSetConfigurators(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
